import {useCallback, useEffect, useRef, useState} from 'react'
import {getScrollableParent} from '@github-ui/get-scrollable-parent'
import {BOTTOM_SENTINEL_HEIGHT, PROGRAMMATIC_SCROLL_DELTA_THRESHOLD} from '../constants'

interface UseStickySidebarProps {
  topOffset: string
  bottomPadding: number
}

/**
 * A hook that provides sticky sidebar behavior with smart positioning based on scroll direction.
 *
 * Uses two complementary techniques:
 * 1. Scroll event handling (with requestAnimationFrame throttling) to detect scroll direction (up/down)
 * 2. IntersectionObserver with "sentinel" elements to efficiently detect when the top/bottom
 *    edges of the sidebar enter or exit the viewport
 *
 * This combination allows the sidebar to behave differently when scrolling up vs down,
 * creating a natural, predictable experience with minimal performance impact.
 */
export function useStickySidebar({topOffset, bottomPadding}: UseStickySidebarProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [styles, setStyles] = useState<React.CSSProperties>({})
  const observerRef = useRef<IntersectionObserver | null>(null)
  const scrollDirection = useRef<'up' | 'down'>('down')
  const isTopInViewport = useRef(false)
  const isBottomInViewport = useRef(false)
  const isScrolledInitially = useRef(false)
  const anySentinelsCrossed = useRef(false)
  const firstTimeDirectionChanged = useRef(false)
  const scrollableParent = useRef<Element | Window | null>(null)

  const scrollableParentTop = useRef<number>(0)
  const parentHeight = useRef<number>(0)
  const elementTop = useRef<number>(0)
  const elementHeight = useRef<number>(0)

  const initialElementOffsetTop = useRef<number>(0)
  const topOffsetSentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return
    const scrollableParentElement = getScrollableParent(element)
    if (scrollableParentElement === document.documentElement) {
      // If the scrollable parent is the document element, we want to use the body instead
      scrollableParent.current = window
      scrollableParentTop.current = 0
    } else {
      scrollableParent.current = scrollableParentElement ?? null
      scrollableParentTop.current = scrollableParentElement?.getBoundingClientRect().top ?? 0
    }
  }, [ref])

  // Main effect for updating styles based on scroll and viewport position
  const updateStyles = useCallback(() => {
    const element = ref.current
    if (!element) return

    // calculate the top offset of the element relative to its parent
    // must be greater than zero (so it doesn't stick out the top of the parent) and avoid extending past the bottom of the parent (if possible)
    const elementOffsetTop = Math.max(
      Math.min(element.offsetTop - initialElementOffsetTop.current, parentHeight.current - elementHeight.current),
      0,
    )

    // If the user refreshes the page already scrolled down,
    // we want to position the sidebar at the top of the viewport
    if (isScrolledInitially.current) {
      // We set the direction to 'up' since the style matches that behavior
      // We want that to change as soon as the user scrolls down
      scrollDirection.current = 'up'
      setStyles({position: 'sticky', top: topOffset})
      return
    }

    // When the sidebar fits entirely in viewport - simple sticky positioning
    if (isTopInViewport.current && isBottomInViewport.current) {
      setStyles({position: 'sticky', top: topOffset})
      return
    }

    // sidebar is longer than viewport
    if (scrollDirection.current === 'up') {
      // When scrolling up, stick to top if top edge is visible
      if (isTopInViewport.current) {
        setStyles({position: 'sticky', top: topOffset})
      } else {
        setStyles({position: 'relative', top: `${elementOffsetTop}px`})
      }
    } else {
      // If the sidebar is inside a scrollable parent that is not the body, we need to account for that
      // because getBoundingClientRect returns the position relative to the viewport.
      // If the scrollable parent top is negative (meaning above the viewport), it's most likely the window itself and we don't want to adjust the position.
      const scrollableParentOffset = Math.max(scrollableParentTop.current, 0)
      // When scrolling down, stick to current position if bottom edge is visible
      if (isBottomInViewport.current) {
        setStyles({
          position: 'sticky',
          top: `${elementTop.current - scrollableParentOffset}px`,
        })
      } else {
        setStyles({position: 'relative', top: `${elementOffsetTop}px`})
      }
    }
  }, [topOffset, ref, parentHeight, scrollableParentTop])

  // Helper function to determine if element edges are in the viewport
  const updateViewportCrossingThresholds = useCallback(
    (entry: IntersectionObserverEntry) => {
      const isTopSentinel = entry.target.id === 'sticky-sidebar-top-sentinel'

      anySentinelsCrossed.current = true

      if (isTopSentinel) {
        isTopInViewport.current = entry.isIntersecting
        elementTop.current = entry.boundingClientRect.top
      } else {
        isBottomInViewport.current = entry.isIntersecting
        // To avoid issues when scrolling down fast, we give the bottom sentinel some extra height
        // and adjust the positioning based on where that intersection was detected.
        elementTop.current =
          entry.boundingClientRect.top - elementHeight.current + Math.floor(entry.intersectionRect.height)
      }

      // This bit is to avoid overriding the styles set by the effect that is triggered
      // when the page is loaded scrolled down already (since it causes the sentinels to fire).
      if (isScrolledInitially.current) return

      updateStyles()
    },
    [updateStyles],
  )

  // Initial check if the page is loaded scrolled down already, if so we want the
  // sidebar to be positioned at the top of the viewport
  useEffect(() => {
    const element = ref.current
    if (!element) return
    // if we are already scrolled down when we load the page past the sidebar's top edge,
    // we need to figure out where we are in relation to the sidebar
    if (element.getBoundingClientRect().top < 0) {
      isScrolledInitially.current = true
      updateStyles()
    }
  }, [updateStyles])

  const getParentYOffset = useCallback(() => {
    if (!scrollableParent.current) return 0
    if (scrollableParent.current instanceof Window) {
      return scrollableParent.current.scrollY
    }
    return scrollableParent.current.scrollTop
  }, [scrollableParent])

  // Detects and tracks scroll direction (up or down) using throttled scroll events
  // Since this is called A LOT, we don't want any expensive DOM operations here.
  useEffect(() => {
    const sensitivityThreshold = 10 // Minimum scroll distance to consider a direction change
    let ticking = false // Used for requestAnimationFrame throttling

    if (!scrollableParent.current) return
    const currentScrollableParent = scrollableParent.current
    let lastScrollY = getParentYOffset()

    // Updates scroll direction state based on current vs previous scroll position
    const updateScrollDir = () => {
      const scrollY = getParentYOffset()
      const scrollDelta = Math.abs(scrollY - lastScrollY)

      // If the scroll delta is large enough, check if we are below the sidebar
      // This is an indication that we scrolled down programmatically with a deep link
      if (scrollDelta > PROGRAMMATIC_SCROLL_DELTA_THRESHOLD) {
        isScrolledInitially.current = true
        updateStyles()
      } else {
        // only reset this if we hit one of the sentinels or this is not the first time we change direction
        // this avoids a reset because of a small programmatic scroll upwards after loading the page
        if (anySentinelsCrossed.current || firstTimeDirectionChanged.current) {
          isScrolledInitially.current = false
        }
      }

      // Ignore small movements (reduces jitter)
      if (scrollDelta < sensitivityThreshold) {
        ticking = false
        return
      }

      const newScrollDirection = scrollY > lastScrollY ? 'down' : 'up'
      // Set direction based on scroll position delta
      if (scrollDirection.current !== newScrollDirection) {
        scrollDirection.current = newScrollDirection
        firstTimeDirectionChanged.current = true
        updateStyles() // Update styles only if direction changed
      }
      lastScrollY = scrollY > 0 ? scrollY : 0
      ticking = false
    }

    // Throttle scroll event handling with requestAnimationFrame
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDir)
        ticking = true
      }
    }

    // Register scroll listener
    // This is needed to detect scroll direction changes, and cannot be done with IntersectionObserver alone.
    // eslint-disable-next-line github/prefer-observers
    currentScrollableParent.addEventListener('scroll', onScroll)

    return () => currentScrollableParent.removeEventListener('scroll', onScroll)
  }, [updateStyles, ref, scrollableParent, getParentYOffset])

  // Create and observe sentinel elements for detecting viewport intersection
  useEffect(() => {
    const element = ref.current
    if (!element) return
    const parentElement = element.parentElement
    if (!parentElement) return

    // Create invisible sentinel elements to detect when element enters/exits viewport
    const topSentinel = document.createElement('div')
    const bottomSentinel = document.createElement('div')

    // Position top sentinel at the top edge (accounting for header)
    topSentinel.style.position = 'absolute'
    topSentinel.style.top = `calc(-1 * ${topOffset})`
    topSentinel.style.height = '1px'
    topSentinel.style.width = '1px'
    topSentinel.style.pointerEvents = 'none'
    topSentinel.id = 'sticky-sidebar-top-sentinel'

    // Position bottom sentinel at the bottom edge
    bottomSentinel.style.position = 'absolute'
    bottomSentinel.style.bottom = `-${BOTTOM_SENTINEL_HEIGHT}px`
    bottomSentinel.style.height = `${BOTTOM_SENTINEL_HEIGHT}px`
    bottomSentinel.style.width = '0'
    bottomSentinel.style.pointerEvents = 'none'
    bottomSentinel.id = 'sticky-sidebar-bottom-sentinel'

    // Add sentinels to DOM
    element.appendChild(topSentinel)
    element.appendChild(bottomSentinel)

    // IntersectionObserver triggers our update function when sentinels cross viewport boundaries
    observerRef.current = new IntersectionObserver(entries => {
      for (const entry of entries) {
        updateViewportCrossingThresholds(entry)
      }
    })

    // Start observing both sentinels
    observerRef.current.observe(topSentinel)
    observerRef.current.observe(bottomSentinel)

    const topOffsetSentinel = document.createElement('div')

    // Position top sentinel at the top edge (accounting for header)
    topOffsetSentinel.style.position = 'relative'
    topOffsetSentinel.style.height = '0px'
    topOffsetSentinel.style.width = '0px'
    topOffsetSentinel.style.pointerEvents = 'none'

    // Add the top offset sentinel to the parent element
    // just before the sidebar element
    parentElement.insertBefore(topOffsetSentinel, element)
    topOffsetSentinelRef.current = topOffsetSentinel

    // Clean up observers and DOM elements on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }

      // Only try to remove if the elements are still in the DOM
      if (topSentinel.parentNode === element) {
        element.removeChild(topSentinel)
      }

      if (bottomSentinel.parentNode === element) {
        element.removeChild(bottomSentinel)
      }

      if (topOffsetSentinelRef.current?.parentNode === parentElement) {
        parentElement.removeChild(topOffsetSentinelRef.current)
      }
    }
  }, [topOffset, updateViewportCrossingThresholds, ref])

  // measure and store the height of the sidebar element
  useEffect(() => {
    if (!ref.current) return
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        elementHeight.current = entry.contentRect.height + bottomPadding
      }
    })
    resizeObserver.observe(ref.current)
    return () => resizeObserver.disconnect() // clean up
  }, [ref, bottomPadding])

  // Observe parent element resize to update styles accordingly
  // This is useful in context of layout shifts as more data is loaded
  useEffect(() => {
    if (!ref.current || !ref.current.parentElement) return
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        parentHeight.current = entry.contentRect.height
      }
      updateStyles()
    })
    resizeObserver.observe(ref.current.parentElement)
    return () => resizeObserver.disconnect() // clean up
  }, [updateStyles, ref])

  useEffect(() => {
    if (!ref.current || !ref.current.parentElement) return
    const resizeObserver = new ResizeObserver(_entries => {
      initialElementOffsetTop.current = topOffsetSentinelRef.current?.offsetTop || 0
    })
    resizeObserver.observe(document.body)
    return () => resizeObserver.disconnect() // clean up
  }, [ref])

  // Return the ref to attach to the element and the calculated styles to apply
  return {styles, ref}
}
